import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  Card, CardBody, CardSubtitle, CardText, CardTitle,
} from 'reactstrap';

import TradeRuleTooltip from 'src/components/TradeRule/TradeRuleTooltip';
import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import { getStringAndNumericFormattedDate } from 'src/util/i18n/helpers';
import { buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';
import { getPriceRange } from 'src/util/tradeRule';

/**
 * Renders a card component displaying trade rule information for residual trade rules.
 * @param {object} props
 * @param {object} props.rule - TradeRule object from GraphQL (TradeRule type)
 * @param {object} props.property - Property object from GraphQL (Property type)
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} props.tradeDirection
 * @returns {React.ReactElement} - Residual Trade Rule card component.
 */
function ResidualTradeRule(props) {
  const intl = useIntl();
  const { rule, property, tradeDirection } = props;
  const {
    uuid: ruleId, start, finish, meter, clauses,
  } = rule || {};
  const { title: tradePointTitle } = meter || {};
  const { uuid: propertyId } = property || {};
  const { minimum, maximum } = getPriceRange(rule) || {};
  const clauseNodes = clauses ? getNodesFromEdges(clauses) : [];

  const approvedTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rule.buy.approved.title', defaultMessage: 'Approved' });
  const furtherDetailsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rule.buy.further_details', defaultMessage: 'further details' });
  const copyTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rule.copy_title', defaultMessage: 'Copy trade rule id to the clipboard' });
  const copySuccessMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rule.copy.success.message', defaultMessage: 'Trade rule id copied to the clipboard.' });
  const tradeRuleIdLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rule.id.label', defaultMessage: 'Trade Rule ID:' });
  return (
    <Card className="mb-4 mt-4" key={`trade-rules-${ruleId}`}>
      <TradeRuleTooltip
        tradeDirection={tradeDirection}
        ruleId={ruleId}
        clauseNodes={clauseNodes}
      >
        {(cardBodyId) => (
          <CardBody id={cardBodyId} style={clauseNodes.length > 0 ? { cursor: 'help' } : undefined} tabIndex={0}>
            <CardTitle tag="h4">
              {tradePointTitle}
            </CardTitle>
            <CardSubtitle tag="h5" className="mb-2">
              {approvedTitle}
            </CardSubtitle>
            <CardText>
              {buildTradeRulePriceInformation({
                detailsLink: <Link to={`/properties/${propertyId}/trade-rules/${ruleId}`}>{furtherDetailsLabel}</Link>,
                direction: tradeDirection,
                maximum,
                minimum,
                intl,
                mode: APIConfig().MODE,
              })}
              <br />
              <>
                <span className="me-1">{tradeRuleIdLabel}</span>
                <UUID
                  copyTitle={copyTitle}
                  copySuccessMsg={copySuccessMsg}
                  uuid={ruleId}
                  url={`/properties/${propertyId}/trade-rules/${ruleId}`}
                />
              </>
              <br />
              {rule?.finish && intl.formatMessage(
                { id: 'trade_rule.trade_rule_active.residual_trade_rule.buy.start_and_finish.text', defaultMessage: 'From {startDate} to {finishDate}' },
                {
                  startDate: getStringAndNumericFormattedDate(intl, start),
                  finishDate: getStringAndNumericFormattedDate(intl, finish),
                },
              )}
              {!rule?.finish && intl.formatMessage(
                { id: 'trade_rule.trade_rule_active.residual_trade_rule.buy.start_only.text', defaultMessage: 'From {startDate} onwards' },
                { startDate: getStringAndNumericFormattedDate(intl, start) },
              )}
            </CardText>
          </CardBody>
        )}
      </TradeRuleTooltip>
    </Card>
  );
}

export default ResidualTradeRule;

ResidualTradeRule.propTypes = {
  rule: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  tradeDirection: PropTypes.string,
};

ResidualTradeRule.defaultProps = {
  rule: null,
  property: null,
  tradeDirection: TRADE_DIRECTION_BUY,
};
